import { capitalize } from 'lodash';
import { Plus } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AgreementFormEnum, EscrowPartyTypeEnum } from 'src/graphql/schema';

import { RepresentativesTable, RepresentativeModalActions, EmptyRepresentatives } from '.';

import { RepresentativeContextProvider, useRepresentative, type IRepresentativeContextProps } from '../model';

type Props = IRepresentativeContextProps & {
  suggestedChangesNode?: React.ReactNode;
};

const Container = ({ suggestedChangesNode }: { suggestedChangesNode?: React.ReactNode }) => {
  const { addRepresentative, role, currentPolicyRole, representatives, agreementType } = useRepresentative();

  let title = capitalize(role) + ' representatives';

  if (
    agreementType === AgreementFormEnum.Tripartite ||
    (agreementType === AgreementFormEnum.Bipartite && role === EscrowPartyTypeEnum.Depositor)
  )
    title += ' & signatories';

  return (
    <Card>
      <CardHeader className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <CardTitle>{title}</CardTitle>
          {currentPolicyRole?.update && Boolean(representatives.length) && (
            <Button variant="outline" size="sm" onClick={addRepresentative} className="shrink-0">
              <Plus /> Add
            </Button>
          )}
        </div>
        {agreementType === AgreementFormEnum.Bipartite && role === EscrowPartyTypeEnum.Beneficiary ? (
          <CardDescription>Who are the beneficiary key points of contact for this escrow?</CardDescription>
        ) : (
          <CardDescription>
            Who are the key points of contact for this escrow? You must add at least one representative, and at least
            one must be designated as a signatory.
          </CardDescription>
        )}
      </CardHeader>
      <CardContent>
        {suggestedChangesNode}
        {representatives.length ? <RepresentativesTable /> : <EmptyRepresentatives />}
      </CardContent>
    </Card>
  );
};

const RepresentativesContainer = ({ suggestedChangesNode, ...rest }: Props) => {
  return (
    <RepresentativeContextProvider {...rest}>
      <Container suggestedChangesNode={suggestedChangesNode} />
      <RepresentativeModalActions />
    </RepresentativeContextProvider>
  );
};

export default RepresentativesContainer;
